import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import DoDisturbOutlinedIcon from '@mui/icons-material/DoDisturbOutlined';

import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUILoadingLinear } from '../../../atomic/atoms';
import { ItemAcceptance, ItemCancellation } from './components';

import '../../style/studio.scss';

const TABS = [
    {
        id: 0,
        labelKey: 'studio.itemAcceptance',
        icon: AssignmentTurnedInOutlinedIcon,
        accent: '#2563EB',
        accentBg: '#EFF6FF',
        accentBorder: '#BFDBFE',
    },
    {
        id: 1,
        labelKey: 'studio.itemCancellation',
        icon: DoDisturbOutlinedIcon,
        accent: '#DC2626',
        accentBg: '#FEF2F2',
        accentBorder: '#FCA5A5',
    },
];

export const ItemAcceptanceCancellation = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);

    const { t } = useTranslation();

    const handleChangeTabs = (_: any, activeTabIndex: number): void => {
        setActiveTab(activeTabIndex);
    };

    const activeMeta = TABS.find((tab) => tab.id === activeTab) ?? TABS[0];

    return (
        <SCUIContent>
            <Box sx={{ padding: '16px' }}>
                <SCUIBreadCrumb onlyTitle={t('studio.itemAcceptanceCancellation.header')} />
                <SCUILoadingLinear display={isLoading} />

                <Paper
                    elevation={0}
                    sx={{
                        mt: 2,
                        borderRadius: '14px',
                        border: '1px solid #EDEFF2',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 6px 24px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            pt: 1.25,
                            pb: 0,
                            gap: 2,
                            borderBottom: '1px solid #EDEFF2',
                            backgroundColor: '#FAFAFA',
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={handleChangeTabs}
                            slotProps={{
                                indicator: {
                                    sx: {
                                        height: 3,
                                        borderRadius: '3px 3px 0 0',
                                        backgroundColor: activeMeta.accent,
                                        transition: 'all 0.2s ease',
                                    },
                                },
                            }}
                            sx={{
                                minHeight: 56,
                                '& .MuiTabs-flexContainer': {
                                    gap: 0.75,
                                },
                                '& .MuiTab-root': {
                                    minHeight: 56,
                                    px: 2.25,
                                    py: 1.25,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    borderRadius: '8px 8px 0 0',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        color: '#374151',
                                        backgroundColor: 'rgba(0,0,0,0.03)',
                                    },
                                },
                                '& .Mui-selected': {
                                    fontWeight: 700,
                                },
                            }}
                        >
                            {TABS.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <Tab
                                        key={tab.id}
                                        value={tab.id}
                                        disableRipple
                                        icon={
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: isActive ? tab.accent : '#9CA3AF',
                                                    backgroundColor: isActive ? tab.accentBg : 'transparent',
                                                    border: `1px solid ${isActive ? tab.accentBorder : 'transparent'}`,
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                <Icon sx={{ fontSize: '1.28rem' }} />
                                            </Box>
                                        }
                                        iconPosition="start"
                                        label={t(tab.labelKey)}
                                        sx={{
                                            '&.Mui-selected': {
                                                color: tab.accent,
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Tabs>

                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, pr: 1 }}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: activeMeta.accent,
                                    boxShadow: `0 0 0 4px ${activeMeta.accentBg}`,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    color: '#6B7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {t(activeMeta.labelKey)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ backgroundColor: '#fff' }}>
                        {activeTab === 0 && <ItemAcceptance setIsLoading={setIsLoading} />}
                        {activeTab === 1 && <ItemCancellation setIsLoading={setIsLoading} />}
                    </Box>
                </Paper>
            </Box>
        </SCUIContent>
    );
};
